import { useState, useEffect, useCallback } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getWeeklyStrainBreakdownData } from '../../supabase-client'

export default function WeeklyStrainBreakdownTable({ loading: externalLoading }) {
  const [strains, setStrains] = useState([])
  const [rows, setRows] = useState([])
  const [totals, setTotals] = useState({})
  const [loading, setLoading] = useState(false)

  const loadData = useCallback(async () => {
    if (externalLoading) return
    setLoading(true)
    try {
      const result = await getWeeklyStrainBreakdownData()
      setStrains(result.strains)
      setRows(result.rows)
      setTotals(result.totals)
    } catch (error) {
      console.error('Error loading weekly strain breakdown:', error)
    } finally {
      setLoading(false)
    }
  }, [externalLoading])

  useEffect(() => {
    loadData()
  }, [loadData])

  const isLoading = externalLoading || loading

  const formatKg = (kg) => {
    if (!kg) return '—'
    return `${(Math.round(kg * 10) / 10).toFixed(1)}`
  }

  const formatDate = (date) => date.toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  })

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 animate-pulse rounded" />
        ))}
      </div>
    )
  }

  if (strains.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No strain sales data available
      </div>
    )
  }

  return (
    <div className="border rounded-lg overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="text-left">Week</TableHead>
            <TableHead className="text-left">Starting</TableHead>
            {strains.map(strain => (
              <TableHead key={strain} className="text-right">{strain}</TableHead>
            ))}
            <TableHead className="text-right font-semibold">Total (kg)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => {
            const rowTotal = strains.reduce((sum, s) => sum + (row.strains[s] || 0), 0)
            return (
              <TableRow key={row.weekKey} className="hover:bg-gray-50">
                <TableCell className="font-medium">{row.weekNumber}</TableCell>
                <TableCell>{formatDate(row.weekStart)}</TableCell>
                {strains.map(strain => (
                  <TableCell key={strain} className="text-right">
                    {formatKg(row.strains[strain])}
                  </TableCell>
                ))}
                <TableCell className="text-right font-semibold">
                  {rowTotal > 0 ? formatKg(rowTotal) : '—'}
                </TableCell>
              </TableRow>
            )
          })}
          <TableRow className="bg-gray-50 font-semibold">
            <TableCell colSpan={2}>Total</TableCell>
            {strains.map(strain => (
              <TableCell key={strain} className="text-right">
                {formatKg(totals[strain])}
              </TableCell>
            ))}
            <TableCell className="text-right">
              {formatKg(Object.values(totals).reduce((s, v) => s + v, 0))}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
